import { tokens } from './tokens.js';
import { THEME_LIGHT, THEME_DARK } from './vuetifyThemes.js';

/**
 * The chart colours for one theme mode — the framework-free half of the theme
 * package. No Vue, no Vuetify, no DOM: callable from a Vuex getter, a plain
 * helper or Node.
 *
 * Anything that is not exactly {@link THEME_DARK} falls back to light, so an
 * uninitialised or `undefined` theme renders dark-on-light rather than the
 * invisible white-on-light it used to (the store's old theme-init bug).
 *
 * Returns a NEW object on every call — callers may mutate their copy (e.g.
 * spread it into an ECharts option) without poisoning the tokens. `dataZoom` is
 * copied too, for the same reason; `palette`/`series` are the shared token
 * arrays (read them, do not mutate).
 *
 * The five mode-independent chart entries — `series`, `seriesFallback`,
 * `axisPointerLabel`, `dataZoom`, `loadingOverlay` — are carried here so a chart
 * component needs exactly one colour source (S11); so is `boxPlot` (copied, like
 * `dataZoom`), the box plot base's group (IA_ABSORPTION_PLAN 12d), and so is
 * `sparkline` — the spark cards' fallbacks for THIS mode (`chart.sparkline[mode]`,
 * copied; IA_ABSORPTION_PLAN 13), and so is `gantt` — `@rdrive/plugin-gantt`'s group for THIS
 * mode (`chart.gantt[mode]`, copied; IA_ABSORPTION_PLAN 13b, per mode since 2026-10-13), and so
 * is asset-management's palette (AM_ABSORPTION_PLAN B3): `categoricalByMode` and `sequential` for
 * THIS mode, `semantic`, `dimOpacity` and `pin` (all copied).
 *
 * @param {string} [mode=THEME_LIGHT] theme name — `'light'` or `'dark'`
 * @returns {{ fontColor: string, background: string, splitLineColor: string,
 *             palette: string[], othersColor: string, unassignedColor: string,
 *             series: string[],
 *             seriesFallback: string, axisPointerLabel: string,
 *             dataZoom: { background: string, dataBackground: string,
 *                         filler: string, handle: string },
 *             loadingOverlay: string,
 *             boxPlot: import('./tokens.js').BoxPlotTokens,
 *             sparkline: import('./tokens.js').SparklineModeTokens,
 *             gantt: import('./tokens.js').GanttTokens,
 *             categoricalByMode: string[], sequential: string[],
 *             semantic: import('./tokens.js').SemanticTokens, dimOpacity: number,
 *             pin: import('./tokens.js').PinTokens }}
 */
export function chartTheme(mode = THEME_LIGHT) {
  const m = mode === THEME_DARK ? THEME_DARK : THEME_LIGHT;
  return {
    ...tokens.chart[m],
    palette: tokens.chart.categorical,
    othersColor: tokens.chart.othersColor,
    unassignedColor: tokens.chart.unassignedColor,
    series: tokens.chart.series,
    seriesFallback: tokens.chart.seriesFallback,
    axisPointerLabel: tokens.chart.axisPointerLabel,
    dataZoom: { ...tokens.chart.dataZoom },
    loadingOverlay: tokens.chart.loadingOverlay,
    boxPlot: { ...tokens.chart.boxPlot },
    sparkline: { line: tokens.chart.sparkline[m].line, gradient: [...tokens.chart.sparkline[m].gradient] },
    gantt: { ...tokens.chart.gantt[m] },
    // asset-management's palette (AM_ABSORPTION_PLAN B3): this mode's 6-colour categorical and
    // 5-step sequential ramp, and the mode-independent outcome colours, dim opacity and pin
    categoricalByMode: [...tokens.chart.categoricalByMode[m]],
    sequential: [...tokens.chart.sequential[m]],
    semantic: { ...tokens.chart.semantic },
    dimOpacity: tokens.chart.dimOpacity,
    pin: { ...tokens.chart.pin },
  };
}
